import moment from 'moment';
import { ContractNotificationEmailHTML } from 'notification_templates/ContractCreatedEmail';
import { useSendContractNotification } from 'routes/Pages/TimberSalesPricingList/CustomHooks/useSendContractNotification';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';

const RECIPIENTS = [
  'accounts@worrellharvesting.ie',
  'ashish.garg@dscission.com',
  '1b4c4a2d.worrellharvesting.ie@emea.teams.ms',
];

export function useSendSageContractNotification() {
  const { sendNotification } = useSendContractNotification();

  const sendSageContractNotification = async (subject, contractUniqueID, contractRowData = {}, isAddendum = false) => {
    try {
      const [optionsRes, contractRes] = await Promise.all([
        customAxios.get(`/Ops/GetContractsOptions/${contractUniqueID}`, { headers: authHeader() }),
        customAxios.get(`/Ops/GetContracts?contractUniqueID=${contractUniqueID}`, { headers: authHeader() }),
      ]);

      const options = optionsRes.data ?? {};
      const contractFull = contractRes.data?.[0] ?? {};

      const cert = options.certificationInfo?.[0] ?? {};
      const invoicingRates = options.invoicingRates?.[0] ?? {};
      const coillteInfo = options.coillteInfo?.[0] ?? {};

      const siteAssessmentID = contractRowData.siteAssessmentID ?? contractFull.siteAssessmentID;

      let siteInfo = {};
      let productPricing = [];
      let notificationContactData = { email: '', phone: '' };

      if (siteAssessmentID) {
        const [siteRes] = await Promise.all([
          customAxios.get(`/CRM/GetCRMSiteDetails?siteAssessmentId=${siteAssessmentID}`, { headers: authHeader() }),
        ]);

        const crmData = siteRes.data?.[0] ?? {};

        siteInfo = {
          siteAssessmentType: crmData.siteAssessmentType || '—',
          siteAssessmentCategory: crmData.siteAssessmentCategory || '—',
          landownerName: crmData.ownerName || '—',
          address: crmData.address1 || '—',
          siteLocation: crmData.siteDetailsRef || '—',
          totalArea: crmData.siteVolAreaInHa || '—',
          whNumber: crmData.wH_Number || '—',
          fellingLicenceNo: crmData.licenseNo || '—',
        };

        // ── Resolve which pricing version to query ──────────────────────────
        // Strip trailing annotations like "(Default version)" and whitespace
        const cleanVersion = raw => {
          if (!raw || typeof raw !== 'string') return '';
          const trimmed = raw.replace(/\(.*?\)/g, '').trim(); // drop "(...)" suffix
          return trimmed;
        };

        const primaryVersion = cleanVersion(contractFull.pricingVersion);
        const fallbackVersion = cleanVersion(contractFull.previousPricingVersion);

        let currentPricingRes;
        let previousPricingRes = null;

        if (primaryVersion) {
          const requests = [
            customAxios.get(`/Ops/GetTimberPricing?pricingVersion=${encodeURIComponent(primaryVersion)}`, {
              headers: authHeader(),
            }),
          ];
          if (fallbackVersion && fallbackVersion !== primaryVersion) {
            requests.push(
              customAxios
                .get(`/Ops/GetTimberPricing?pricingVersion=${encodeURIComponent(fallbackVersion)}`, {
                  headers: authHeader(),
                })
                .catch(e => {
                  console.warn('Could not fetch previous pricing version:', e);
                  return null;
                }),
            );
          }
          const [curRes, prevRes] = await Promise.all(requests);
          currentPricingRes = curRes;
          previousPricingRes = prevRes ?? null;
        } else if (fallbackVersion) {
          currentPricingRes = await customAxios.get(
            `/Ops/GetTimberPricing?pricingVersion=${encodeURIComponent(fallbackVersion)}`,
            { headers: authHeader() },
          );
        } else {
          currentPricingRes = await customAxios.get(`/Ops/GetTimberPricing?siteAssessmentID=${siteAssessmentID}`, {
            headers: authHeader(),
          });
        }

        try {
          const notifyRes = await customAxios.get(`/Ops/GetContractsUsersToNotify?contractUniqueID=${contractUniqueID}`, {
            headers: authHeader(),
          });

          const notifyRecords = notifyRes.data ?? [];

          const firstEmail = notifyRecords.find(
            r => r.notificationType?.toLowerCase() === 'email' && r.emailOrPhone?.trim(),
          );
          const firstPhone = notifyRecords.find(r => r.notificationType?.toLowerCase() === 'sms' && r.emailOrPhone?.trim());

          notificationContactData = {
            email: firstEmail?.emailOrPhone ?? '',
            phone: firstPhone?.emailOrPhone ?? '',
          };
        } catch (notifyErr) {
          console.warn('Could not fetch contract notification contacts:', notifyErr);
          // non-fatal — email still sends, just without contact info
        }

        const round2 = v => (v != null && !isNaN(v) ? Number(v).toFixed(2) : null);
        const norm = v => (typeof v === 'string' ? v.trim().toLowerCase() : v);

        // Group previous rows by product (normalized). A product can have multiple
        // destinations, so keep an array per product and consume matches as we go.
        const previousByProduct = new Map();
        (previousPricingRes?.data ?? []).forEach(item => {
          const key = norm(item.product);
          if (!previousByProduct.has(key)) previousByProduct.set(key, []);
          previousByProduct.get(key).push(item);
        });

        productPricing = (currentPricingRes.data ?? []).map(item => {
          const candidates = previousByProduct.get(norm(item.product)) ?? [];

          // Prefer an exact destination match if one exists; otherwise fall back to
          // the first remaining candidate for that product (destination itself changed).
          let matchIndex = candidates.findIndex(c => norm(c.destination) === norm(item.destination));
          if (matchIndex === -1 && candidates.length > 0) matchIndex = 0;

          const prev = matchIndex !== -1 ? candidates[matchIndex] : null;
          if (matchIndex !== -1) candidates.splice(matchIndex, 1); // consume so it isn't reused

          const isNew = !prev;

          const changed = prev
            ? {
                destination: norm(item.destination) !== norm(prev.destination),
                haulageRate: round2(item.haulage) !== round2(prev.haulage),
                harvestingRate: round2(item.harvestingCostsPerTonne) !== round2(prev.harvestingCostsPerTonne),
                extractionRate: round2(item.extractionCosts) !== round2(prev.extractionCosts),
                purchasePrice: round2(item.purchasesPrice) !== round2(prev.purchasesPrice),
              }
            : {};

          return {
            product: item.product,
            destination: item.destination,
            haulageRate: item.haulage,
            harvestingRate: item.harvestingCostsPerTonne,
            extractionRate: item.extractionCosts,
            purchasePrice:
              item.purchasesPrice != null && !isNaN(item.purchasesPrice) ? Number(item.purchasesPrice).toFixed(2) : '—',
            changed,
            isNew,
          };
        });
      }

      let uploadedDocuments = [];
      try {
        const contractDocsResponse = await customAxios.get(`/Ops/GetContractsDocs/${contractUniqueID}`, {
          headers: authHeader(),
        });

        uploadedDocuments = (contractDocsResponse.data ?? []).map(doc => ({
          name: doc.documentName,
          docType: doc.docType,
          url: `https://logwatch-api.myreports.online/api/Ops/GetContractsFilePublic/${contractUniqueID}/${doc.id}`,
        }));
      } catch (docErr) {
        console.warn('Could not fetch contract docs for links:', docErr);
        // non-fatal — email still sends, just without doc links
      }

      const pricingVersionLabel = contractRowData.pricingVersion
        ? `${contractRowData.pricingVersion} ${contractRowData.timberPricingLabel ?? ''}`.trim()
        : contractFull.pricingVersion ?? '—';

      const data = {
        contractUniqueID,
        contractTitle: contractRowData.wH_NumberName ?? contractFull.wH_NumberName ?? '—',
        createdAt: moment(contractRowData.submissionDate ?? contractFull.submissionDate).format('lll'),
        siteAssessmentRef: siteAssessmentID ?? '—',
        fellingLicenceNo: siteInfo.fellingLicenceNo ?? contractFull.fellingLicenceNo ?? '—',
        pricingVersion: pricingVersionLabel,
        isAddendum,

        // Supplier / site summary
        supplierName: cert.forestGrower ?? contractFull.supplierSPDescription ?? '—',
        landownerName: siteInfo.landownerName ?? contractFull.landOwnerDetails ?? '—',
        address: siteInfo.address ?? contractFull.address ?? '—',
        county: cert.forestSourcingArea ?? '—',
        totalArea: siteInfo.totalArea ?? '—',
        supplierType: cert.supplierType ?? '—',
        harvestedBy: cert.harvestedBy ?? '—',
        confirmedBy: coillteInfo.coillteRateConfirmedBy ?? invoicingRates.harvestingConfirmedBy ?? '',

        notificationContact: {
          email: notificationContactData.email,
          phone: notificationContactData.phone,
        },

        contractDetails: {
          contractType: contractFull.contractType ?? siteInfo.siteAssessmentType ?? '—',
          purchaseType: contractFull.purchaseType ?? siteInfo.siteAssessmentCategory ?? '—',
          startDate: contractFull.startDate ?? '—',
          finishDate: contractFull.finishDate ?? '—',
          labelOfContract: contractFull.wH_NumberName ?? '—',
          vatRate: contractFull.vatRate ?? '—',
          notesToOperators: contractFull.notestoOperators ?? '',
          internalNotes: contractFull.notestoOffice ?? '',
        },

        certificationInfo:
          Object.keys(cert).length > 0
            ? {
                county: cert.forestSourcingArea ?? '—',
                supplierType: cert.supplierType ?? '—',
                validSelfDeclaration: cert.validSelfDeclaration ?? '—',
                biomassSite: cert.biomassSite ?? '—',
                forestGrower: cert.forestGrower ?? '—',
                contractWith: cert.contractWith ?? '—',
                harvestedBy: cert.harvestedBy ?? '—',
                tripartiteAgreement: cert.tripartiteAgreement ?? '—',
                notes: cert.notes ?? '',
                coillteRoadsideRate: coillteInfo.coillteRoadsideRate ?? '',
                shortStakesPremium: coillteInfo.shortStakesPremium ?? '',
                coillteRateConfirmedBy: coillteInfo.coillteRateConfirmedBy ?? '',
              }
            : null,

        productPricing,

        accountDetails: {
          accountHolder: contractFull.accountHolder ?? '',
          bankName: contractFull.bankName ?? '',
          iban: contractFull.iban ?? '',
          bic: contractFull.bic ?? '',
        },

        uploadedDocuments,

        contractUrl: `https://logwatch.myreports.online/siteContracts?contractUniqueID=${contractUniqueID}`,
      };

      const html = ContractNotificationEmailHTML(data);

      const results = await Promise.allSettled(
        RECIPIENTS.map(email =>
          sendNotification({
            sendTo: email,
            subject: subject ?? `Contract Notification – ${contractUniqueID}`,
            body: html,
            deliveryType: 'EMAIL',
            primaryKey: null,
            uniqueRef: contractUniqueID,
            templateType: null,
            rawData: JSON.stringify(data),
            notificationSection: 'Contract',
          }),
        ),
      );

      results.forEach((r, i) => {
        if (r.status === 'rejected' || r.value === false) {
          console.error(`Notification failed for ${RECIPIENTS[i]}:`, r.reason);
        }
      });
    } catch (err) {
      console.error('sendSageContractNotification error:', err);
    }
  };

  return { sendSageContractNotification };
}
